import {
  PAGE_LAYOUT_SLOTS,
  type PageLayout,
  type PageLayoutSlot,
  type PageSpacing,
} from "@loot-os/api-contract";
import { type ComponentType, createElement, Fragment, type ReactNode } from "react";

import type { PageLayoutProps, PageTemplate } from "./contract.js";
import { SectionColumn } from "./shared/SectionColumn.js";

/**
 * Sections and their variants: a section is one thing a page can show (the featured boxes, the
 * FAQ), drawn by one component; a variant is one way of drawing it (a grid, a rail), named by id.
 * A template picks a variant, and the settings it changes, for each of its page's sections by
 * those ids, never by wrapping the component, so every variant is open to every template and,
 * later, to a store's own page. `docs/design-system/templates.md` ("Sections and variants").
 *
 * Reading the types below: `Content` is a section's content (`FaqContent`), `VariantId` the union
 * of its variant ids (`"split" | "centered" | "stacked"`), `Settings` its settings by name and
 * type (`{ shine: boolean }`). `PageContent` is a whole page's content (`HomeContent`), and
 * `Sections` the page's list of definitions as written (`typeof HOME_SECTIONS`).
 */

/** One way a section is drawn, as whoever picks it sees it. */
export interface SectionVariant {
  /** A word or two: "Rail". */
  label: string;
  /** What it looks like and what it suits, in a sentence. */
  description: string;
}

/** One setting a section takes beside its variant: a value its component reads, never markup. */
export interface SectionSetting<Value> {
  label: string;
  description: string;
  /** The component's own default, which a template that names nothing gets. */
  default: Value;
}

/** What a section's component takes beside its content: the variant and its settings. */
export type SectionDrawing<VariantId extends string, Settings> = { variant?: VariantId } & {
  [Name in keyof Settings]?: Settings[Name];
};

/**
 * A section of a page. `Component` takes the section's content plus `variant` and the settings;
 * `variants` lists every variant it draws (a variant it draws but this list leaves out cannot be
 * picked), `defaultVariant` is the one its component draws when given none, and `settings` lists
 * each setting with its default. Declare one with `defineSection`.
 */
export interface SectionDefinition<Content, VariantId extends string, Settings> {
  /** The section's name, as whoever picks it sees it: "Featured boxes". */
  label: string;
  // `NoInfer`: the variant ids are read from `variants` and the settings from `settings`, never
  // from the component's props. The component is then checked against them: a listed variant its
  // `variant` prop doesn't take fails to compile.
  Component: ComponentType<Content & SectionDrawing<NoInfer<VariantId>, NoInfer<Settings>>>;
  variants: { readonly [Id in VariantId]: SectionVariant };
  defaultVariant: NoInfer<VariantId>;
  settings: { readonly [Name in keyof Settings]: SectionSetting<Settings[Name]> };
}

/**
 * Declares a section of `Content`: the variant ids are `variants`' keys and the settings' types
 * come from `settings`, so a template's pick is checked against them. Curried so the content type
 * is named while the rest is inferred: `defineSection<FaqContent>()({ … })`.
 */
export function defineSection<Content>() {
  return <VariantId extends string, Settings = Record<never, never>>(
    definition: SectionDefinition<Content, VariantId, Settings>,
  ): SectionDefinition<Content, VariantId, Settings> => definition;
}

/** The variant ids a section's definition offers: `VariantOf<typeof HOME_SECTIONS.faq>`. */
export type VariantOf<Section> =
  Section extends SectionDefinition<infer _Content, infer VariantId, infer _Settings>
    ? VariantId
    : never;

/** The settings a section's definition takes, by name and type. */
type SettingTypesOf<Section> =
  Section extends SectionDefinition<infer _Content, infer _VariantId, infer Settings>
    ? Settings
    : never;

/**
 * The settings a section's definition takes, each optional; `never` for a section that takes
 * none, so a pick that hands it any setting fails to compile (TypeScript lets anything through an
 * empty object type).
 */
export type SettingsOf<Section> = keyof SettingTypesOf<Section> extends never
  ? never
  : { [Name in keyof SettingTypesOf<Section>]?: SettingTypesOf<Section>[Name] };

/** How a template draws one section: a variant by id and the settings it changes. */
export interface SectionPick<Section> {
  variant?: VariantOf<Section>;
  settings?: SettingsOf<Section>;
}

/** A template's picks for a page's sections: any section it names, by variant and settings. */
export type SectionPicks<Sections> = {
  readonly [Key in keyof Sections]?: SectionPick<Sections[Key]>;
};

/**
 * What a page's list of sections must be: for every key of `PageContent`, a definition whose
 * content is that key's. Each entry is checked against its own variants and settings, read back
 * from the list as written (`Sections`), which is why the list appears on both sides: a single
 * shared shape for every entry would have to accept every variant id as a plain string, and a
 * component that draws only some ids is not assignable to that.
 */
export type PageSections<PageContent, Sections> = {
  [Key in keyof PageContent]-?: Key extends keyof Sections
    ? SectionDefinition<
        NonNullable<PageContent[Key]>,
        VariantOf<Sections[Key]>,
        SettingTypesOf<Sections[Key]>
      >
    : never;
};

/**
 * Declares a page's sections, one for every key of `PageContent` and no other (an extra key is
 * typed `never`, so it fails to compile): the one list every template of the page draws from
 * (`HOME_SECTIONS`). Curried like `defineSection`.
 */
export function definePageSections<PageContent>() {
  return <const Sections extends PageSections<PageContent, Sections>>(
    sections: Sections & { readonly [Extra in Exclude<keyof Sections, keyof PageContent>]: never },
  ): Sections => sections;
}

/**
 * A page template's section components, from the page's sections and the template's picks: each
 * section in its picked variant and settings, or its defaults where the template names nothing.
 * A pick naming a variant or setting the section doesn't declare fails to compile.
 */
export function drawSections<
  PageContent,
  const Sections extends PageSections<PageContent, Sections>,
>(sections: Sections, picks: SectionPicks<Sections>): PageTemplate<PageContent>["sections"] {
  const drawn: Partial<Record<keyof PageContent, ComponentType<never>>> = {};
  for (const key of Object.keys(sections) as (keyof PageContent & keyof Sections & string)[]) {
    const { Component, defaultVariant } = sections[key];
    const pick: SectionPick<Sections[typeof key]> = picks[key] ?? {};
    const variant = pick.variant ?? defaultVariant;
    const settings = pick.settings ?? {};
    // The settings first, so a content key can never be overwritten by one.
    const Drawn = (content: object) =>
      createElement(Component as ComponentType<Record<string, unknown>>, {
        ...settings,
        ...content,
        variant,
      });
    Drawn.displayName = `${Component.displayName ?? Component.name}(${variant})`;
    drawn[key] = Drawn;
  }
  return drawn as PageTemplate<PageContent>["sections"];
}

/**
 * The variant a template draws a section in: its pick, or the section's default where it names
 * none. What an app asks before fetching what one variant alone shows (Home's Forge showcase).
 */
export function drawnVariant<
  Sections extends { readonly [Key in keyof Sections]: { readonly defaultVariant: string } },
  Key extends keyof Sections,
>(
  sections: Sections,
  picks: { readonly [Name in keyof Sections]?: { readonly variant?: string } } | undefined,
  key: Key,
): string {
  return picks?.[key]?.variant ?? sections[key].defaultVariant;
}

/**
 * One section on a page, as a template's list names it: the section, and the variant and
 * settings it is drawn in (its defaults where left out), each checked against the section.
 */
export type SectionEntry<Sections> = {
  [Key in keyof Sections & string]: {
    readonly section: Key;
    readonly variant?: VariantOf<Sections[Key]>;
    readonly settings?: SettingsOf<Sections[Key]>;
  };
}[keyof Sections & string];

/**
 * A page as an ordered list of its sections in the slots of a layout, at a spacing:
 * api-contract's `PageSectionList`, checked against the page's sections. A template's preset is
 * one, and a store's own page will be one.
 */
export type SectionList<Sections> = {
  [L in PageLayout]: {
    readonly layout: L;
    readonly spacing: PageSpacing;
    readonly slots: { readonly [Slot in PageLayoutSlot<L>]: readonly SectionEntry<Sections>[] };
  };
}[PageLayout];

/** Every section a list places, in order: slot by slot, in the order its layout lists them. */
export function listedSections<Sections>(list: SectionList<Sections>): SectionEntry<Sections>[] {
  const slots = list.slots as Readonly<Record<string, readonly SectionEntry<Sections>[]>>;
  return PAGE_LAYOUT_SLOTS[list.layout].flatMap((slot) => slots[slot] ?? []);
}

/** A list's picks: the variant and settings it names for each section it places. */
export function picksOfList<Sections>(list: SectionList<Sections>): SectionPicks<Sections> {
  const picks: Record<string, SectionPick<unknown>> = {};
  for (const { section, variant, settings } of listedSections(list))
    picks[section] = { variant, settings } as SectionPick<unknown>;
  return picks as SectionPicks<Sections>;
}

/**
 * Throws unless the list places each section at most once and every section the page requires
 * (`required`, api-contract's `STOREFRONT_REQUIRED_SECTIONS`) somewhere.
 */
export function assertSectionList<Sections>(
  list: SectionList<Sections>,
  required: readonly string[],
): void {
  const placed = listedSections(list).map((entry) => entry.section);
  const repeated = placed.find((section, i) => placed.indexOf(section) !== i);
  if (repeated) throw new Error(`A page list places "${repeated}" twice.`);
  const missing = required.find((section) => !placed.includes(section as keyof Sections & string));
  if (missing) throw new Error(`A page list leaves out "${missing}", which the page requires.`);
}

/**
 * A page template from the page's sections and a list: each listed section in its variant and
 * settings, placed in its slot in order and laid out by the list's layout (`column`:
 * `SectionColumn`) at its spacing. A section the list leaves out is the template's `omits`: not
 * drawn, and with no marker on the page. Throws, when the module loads, on a list that repeats a
 * section or leaves out a required one.
 */
export function sectionListTemplate<
  PageContent,
  const Sections extends PageSections<PageContent, Sections>,
>(
  sections: Sections,
  list: SectionList<Sections>,
  required: readonly string[],
): PageTemplate<PageContent> & { sectionList: SectionList<Sections> } {
  assertSectionList(list, required);
  const picks = picksOfList(list);
  const placed = new Set<string>(listedSections(list).map((entry) => entry.section));
  const slot = (entries: readonly SectionEntry<Sections>[], drawn: Record<string, ReactNode>) =>
    entries.map((entry) => createElement(Fragment, { key: entry.section }, drawn[entry.section]));
  function SectionListLayout({ sections: drawn }: PageLayoutProps<PageContent>) {
    const byKey = drawn as Record<string, ReactNode>;
    // One layout so far; a page with parts side by side adds its own here.
    return createElement(SectionColumn, {
      spacing: list.spacing,
      lead: slot(list.slots.lead, byKey),
      body: slot(list.slots.body, byKey),
    });
  }
  return {
    Layout: SectionListLayout,
    sections: drawSections<PageContent, Sections>(sections, picks),
    picks: picks as PageTemplate<PageContent>["picks"],
    omits: (Object.keys(sections) as (keyof PageContent & string)[]).filter(
      (key) => !placed.has(key),
    ),
    sectionList: list,
  };
}
